import { type ApolloError, useSubscription } from '@apollo/client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import logger from '@/utils/logger'

import { useUser } from '@/client/context/user'
import type {
  RealtimeConversationMessageFragment,
  RealtimeConversationSingleSubscription,
  RealtimeConversationSingleSubscriptionVariables,
} from '@/client/services/hasura/generated/graphql'

import { REALTIME_CONVERSATION_SUBSCRIPTION } from './useRealtimeConversation.hasura'

const PAGE_SIZE = 15

export default function useRealtimeConversation({
  conversationUuid,
  skip = false,
}: {
  conversationUuid: string
  skip?: boolean
}): {
  loading: boolean
  error: ApolloError | null
  messages: RealtimeConversationMessageFragment[] | null
  conversation: RealtimeConversationSingleSubscription['conversation'][number] | null
  onLoadMore: () => void
  hasMore: boolean
} {
  const currentUser = useUser()
  const initialTimestamp = useMemo(() => new Date(), [])

  const [recentMessagesLimit, setRecentMessagesLimit] = useState(PAGE_SIZE)

  useEffect(() => {
    setRecentMessagesLimit(PAGE_SIZE)
  }, [conversationUuid])

  const loadMore = useCallback(() => {
    setRecentMessagesLimit(lim => lim + PAGE_SIZE)
  }, [])

  const { loading, error, data } = useSubscription<
    RealtimeConversationSingleSubscription,
    RealtimeConversationSingleSubscriptionVariables
  >(REALTIME_CONVERSATION_SUBSCRIPTION, {
    skip,
    variables: {
      currentUserId: currentUser.id,
      limitMessagesOld: recentMessagesLimit,
      whereConversation: {
        external_uuid: { _eq: conversationUuid },
      },
      whereMessagesOld: {
        conversation: { external_uuid: { _eq: conversationUuid } },
        created_at: { _lte: initialTimestamp },
      },
      whereMessagesNew: {
        conversation: { external_uuid: { _eq: conversationUuid } },
        created_at: { _gt: initialTimestamp },
      },
    },
    onError: err => {
      logger.warning('Error fetching realtime conversation', { err, conversationUuid })
    },
  })

  // We need this ref to store the last available data in case of loading more
  // messages, so the user has more than a spinner on their screen
  const lastWorkingData = useRef<typeof data>()
  if (data != null) {
    lastWorkingData.current = data
  }

  const workingData = data ?? lastWorkingData.current ?? null
  const conversation = workingData?.conversation?.[0] ?? null
  const messages =
    conversation != null
      ? conversation.messagesOld
          .slice()
          .reverse()
          .concat(conversation.messagesNew.slice().reverse())
      : null
  const hasMore = conversation != null && conversation.messagesOld.length >= recentMessagesLimit

  return {
    loading,
    error: error ?? null,
    messages,
    conversation: conversation ?? null,
    onLoadMore: loadMore,
    hasMore,
  }
}
